/**
 * @module AnimatedSection
 * @description A reusable section wrapper component that adds scroll-based animations to its children.
 * Uses Framer Motion for smooth scroll animations and intersection observer for triggering.
 * 
 * @typedef {Object} AnimatedSectionProps
 * @property {React.ReactNode} children - The content to be animated
 * @property {string} [className] - Optional CSS classes to apply to the section
 * 
 * @example
 * ```jsx
 * <AnimatedSection className="py-12">
 *   <h2>My Animated Content</h2>
 *   <p>This content will animate when scrolled into view</p>
 * </AnimatedSection>
 * ```
 */

import { motion } from 'framer-motion';
import PropTypes from 'prop-types';
import { cn } from '@lib/utils';

/**
 * AnimatedSection component
 * 
 * @param {AnimatedSectionProps} props - Component props
 * @returns {JSX.Element} Animated section wrapper
 */
export function AnimatedSection({ children, className }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-100px' }}
      transition={{ duration: 0.5, ease: [0.87, 0, 0.13, 1] }}
      className={cn(className)}
    >
      {children}
    </motion.div>
  );
}

AnimatedSection.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string
}; 